import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@tanstack/react-form';
import { Button } from '@/shared/components/ui/Button';
import { useDebouncedRelatedSignals } from '@/shared/hooks';
import {
  IssueType,
  IssueStatus,
  type RelatedSignalsApiResponse,
} from '@momentum/types';
import { ParentGoalSelector } from './components/ParentGoalSelector';
import { AssigneeSelector } from './components/AssigneeSelector';
import { StatusSelector } from './components/StatusSelector';
import { SignalSuggestionsSelector } from './components/SignalSuggestionsSelector';
import { TaskRecurrenceField } from './components/TaskRecurrenceField';
import { ViewerSelector } from './components/ViewerSelector';

import { useAppForm } from '@/shared/components/form/form';
import {
  CreateTaskFormData,
  createTaskSchema,
  type SignalSuggestionItem,
} from './types/tasks.types';

interface TaskFormProps {
  initialValues: CreateTaskFormData;
  isPending: boolean;
  onSubmit: (
    formData: CreateTaskFormData,
    signals: RelatedSignalsApiResponse[],
    hasReceivedSignalSuggestions?: boolean
  ) => Promise<void>;
  onCancel?: () => void;
  submitLabel: string;
  submittingLabel: string;
  /** Current task signals (update only); shown and pre-approved when editing */
  initialApprovedSignals?: RelatedSignalsApiResponse[];
}

export const TaskForm: React.FC<TaskFormProps> = ({
  initialValues,
  isPending,
  onSubmit,
  onCancel,
  submitLabel,
  submittingLabel,
  initialApprovedSignals = [],
}) => {
  const { t } = useTranslation('goals');

  const initialQueryRef = useRef<{
    title: string;
    description: string;
  } | null>({
    title: initialValues.title,
    description: initialValues.description ?? '',
  });

  // Watch form values
  const form = useAppForm({
    defaultValues: initialValues,
    validators: {
      onChange: createTaskSchema,
    },
    onSubmit: async ({ value }) => {
      try {
        // Ensure status is set to TODO for recurring tasks
        const formData: CreateTaskFormData = {
          ...value,
          status: value.recurrence
            ? IssueStatus.TODO
            : value.status || IssueStatus.TODO,
          // Enforce hard invariant: clear startDate when recurrence exists
          startDate: value.recurrence ? undefined : value.startDate,
        };
        await onSubmit(formData, approvedSignals, undefined);

        form.reset();
      } catch (error) {
        console.error('Failed to submit task:', error);
      }
    },
  });

  const { title, description } = useStore(form.store, (state) => ({
    title: state.values.title,
    description: state.values.description ?? '',
  }));

  const initial = initialQueryRef.current;
  const hasTitleOrDescriptionChanged =
    initial != null &&
    (title.trim() !== initial.title.trim() ||
      (description ?? '').trim() !== (initial.description ?? '').trim());

  const { signals, isLoading: isSignalsLoading } = useDebouncedRelatedSignals({
    title,
    description,
    initialQuery: initial ?? undefined,
  });

  const signalsRef = useRef(signals);
  signalsRef.current = signals;
  const signalIdsKey = signals
    .map((l) => l.id)
    .sort()
    .join(',');

  const shouldResetFromFetch =
    hasTitleOrDescriptionChanged || initialApprovedSignals.length === 0;

  const [signalItems, setSignalItems] = useState<SignalSuggestionItem[]>(() =>
    initialApprovedSignals.map((l) => ({
      id: l.id,
      signal: l.signal,
      contextualTitle: l.contextualTitle,
      approved: true,
    }))
  );

  useEffect(() => {
    if (shouldResetFromFetch) {
      setSignalItems(
        signalsRef.current.map((l) => ({
          id: l.id,
          signal: l.signal,
          contextualTitle: l.contextualTitle,
          approved: true,
        }))
      );
    }
  }, [shouldResetFromFetch, signalIdsKey]);

  const approvedSignals = signalItems
    .filter((item) => item.approved)
    .map((item) => ({
      id: item.id,
      signal: item.signal,
      contextualTitle: item.contextualTitle,
    }));

  const handleToggle = (id: string, approved: boolean) => {
    setSignalItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, approved } : item))
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit();
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto -mx-6 px-6 min-h-0">
        <div className="space-y-6 py-2">
          {/* Main goal information */}
          <form.AppField
            name="title"
            children={(field) => <field.TextField label={t('title')} />}
          />

          <form.AppField
            name="description"
            children={(field) => (
              <field.TextAreaField label={t('description')} rows={2} />
            )}
          />

          <TaskRecurrenceField form={form} disabled={isPending} />

          <SignalSuggestionsSelector
            items={signalItems}
            onToggle={handleToggle}
            isLoading={isSignalsLoading}
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <AssigneeSelector form={form} />

            <form.AppField
              name="parent"
              children={(field) => (
                <ParentGoalSelector
                  field={field}
                  disabled={isPending}
                  types={[IssueType.GOAL]}
                />
              )}
            />
          </div>

          <div className="grid grid-cols-1 gap-4">
            <form.AppField
              name="viewers"
              children={(field) => (
                <ViewerSelector field={field} disabled={isPending} />
              )}
            />
          </div>

          {/* Status and Dates */}
          <form.Subscribe
            selector={(state) => ({
              hasRecurrence: !!state.values.recurrence,
            })}
            children={(state) => (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Hide startDate when recurrence is enabled (hard invariant) */}
                {!state.hasRecurrence && (
                  <form.AppField
                    name="startDate"
                    children={(field) => (
                      <field.DateField label={t('startDate')} />
                    )}
                  />
                )}

                {/* Hide dueDate when recurrence is enabled */}
                {!state.hasRecurrence && (
                  <form.AppField
                    name="dueDate"
                    children={(field) => (
                      <field.DateField label={t('dueDate')} />
                    )}
                  />
                )}

                {/* Hide status selector when recurrence is enabled */}
                {!state.hasRecurrence && (
                  <form.AppField
                    name="status"
                    children={(field) => (
                      <StatusSelector field={field} disabled={isPending} />
                    )}
                  />
                )}
              </div>
            )}
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 pb-4 border-t border-gray-200 -mx-6 px-6 bg-white">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isPending}
          >
            {t('cancel')}
          </Button>
        )}
        <Button type="submit" disabled={isPending}>
          {isPending ? submittingLabel : submitLabel}
        </Button>
      </div>
    </form>
  );
};
